import TwoColumnLayout from "layout/TwoColumnLayout";
import { useLoaderData } from "react-router";
import React from "react";
import { GroundTransportDetailSection } from "groundTransport/GroundTransportDetailSection";
import GroundTransportOptionsPanel from "groundTransport/GroundTransportOptionsPanel";
import { GroundTransportActivityPanel } from "./activity/GroundTransportActivityPanel";
import { GroundTransportItinerarySidebarApp } from "show/ItinerarySidebar/app";
import moment from "moment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import { useIsInternal } from "common/SessionContext";

export const GroundTransportDetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.GroundTransportDetailFrontModel>();
  const groundTransportId = viewModel.groundTransport.groundTransportId;
  const queryClient = useQueryClient();
  const isInternalUser = useIsInternal();

  const sidePanelQuery = useQuery({
    queryKey: ["linkedShowsToFlight", "groundTransport", groundTransportId],
    queryFn: async () => {
      const result = await window.CoreApi.GroundTransport.GetSidePanelData(groundTransportId);
      if (result.data.hasErrors) throw new Error(result.data.errors[0].message);
      return result.data.data;
    },
    initialData: {
      groundTransport: viewModel.groundTransport,
      passengers: viewModel.passengers,
      linkedShows: viewModel.linkedShows
    },
    initialDataUpdatedAt: Date.now(),
    staleTime: Infinity,
  });

  const { groundTransport, passengers, linkedShows } = sidePanelQuery.data;
  const refetchSidePanelData = async () => {
    await sidePanelQuery.refetch();
    invalidateGroundTransportsCache(queryClient);
  };

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <GroundTransportDetailSection
        groundTransport={groundTransport}
        linkedShows={linkedShows}
        linkShowToTravel={{
          canLink: groundTransport.permissions.canModify,
          travelType: "groundTransport",
          travelId: groundTransportId,
          appointmentDateTime: groundTransport.dateTime,
          artist: groundTransport.artist,
          linkedShowIds: linkedShows.map(show => show.showId),
          onLinked: refetchSidePanelData
        }}
        passengers={passengers}
        onUpdated={refetchSidePanelData}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <GroundTransportOptionsPanel
        optionsPanelData={viewModel.optionsPanel}
        listUrl={viewModel.listUrl}
        groundTransportId={groundTransportId}
      />
      {isInternalUser && <GroundTransportItinerarySidebarApp
        groundTransportId={groundTransportId}
        artist={groundTransport.artist}
        startDate={moment.utc(groundTransport.dateTime.startDisplay)}
        endDate={moment.utc(groundTransport.dateTime.endDisplay)}
        linkedShowIds={linkedShows.map(ls => ls.showId)}
        queryKeys="groundTransportItinerary"
      />}
      {isInternalUser && <GroundTransportActivityPanel
        config={viewModel.activityPanelConfig}
        groundTransportId={groundTransportId}
      />}
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
}
